import { produce } from 'immer';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getRequiredValidationError,
  getValidationError,
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import Checkbox from '../../../../../../common/widgets/Checkbox';
import Dialog from '../../../../../../common/widgets/Dialog';
import Dropdown from '../../../../../../common/widgets/Dropdown';
import Icons from '../../../../../../common/widgets/Icons';
import Input from '../../../../../../common/widgets/Input';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import {
  Notification,
  NotificationType,
} from '../../../../../../common/widgets/Notification';
import Password from '../../../../../../common/widgets/Password';
import PopoverDialog from '../../../../../../common/widgets/PopoverDialog';
import { CustomerInfoContext } from '../../../../contexts/CustomerInfoContext';
import { DropdownOptionModel } from '../../../../models/Common';
import { CustomerInvoicingAddressModel } from '../../../../models/Customers';
import { ErrorState, ErrorType } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { customerService } from '../../../../services/customer';
import { locationService } from '../../../../services/locations';
import { CnpGdprDialog } from './CnpGdprDialog';
import { InvoicingAddress, InvoicingAddressContextTypes } from './types';

interface InvoicingAddressDialogProps {
  initialData?: InvoicingAddress;
  closeDialog: (
    newCustomerInvoicingAddress?: CustomerInvoicingAddressModel,
  ) => void;
  contextType: InvoicingAddressContextTypes;
  onSaveInCart?: (invoicingAddress: InvoicingAddress) => void;
}

export const InvoicingAddressDialog = (props: InvoicingAddressDialogProps) => {
  const { initialData, closeDialog, contextType, onSaveInCart } = props;
  const { t: translate } = useTranslation();
  const [county, setCounty] = useState(initialData?.county);
  const [counties, setCounties] = useState(initialData?.counties);
  const [filteredCounties, setFilteredCounties] = useState(counties);
  const [city, setCity] = useState(initialData?.city);
  const [cities, setCities] = useState(initialData?.cities);
  const [filteredCities, setFilteredCities] = useState(cities);
  const [citiesFilter, setCitiesFilter] = useState('');
  const [addressLine, setAddressLine] = useState(initialData?.addressLine);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [agreeCnp, setAgreeCnp] = useState(!!initialData?.agreeCnp);
  const [cnp, setCnp] = useState(initialData?.cnp);
  const [errors, setErrors] = useState<ErrorState>({});
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [showGdprDialog, setShowGdprDialog] = useState(false);
  const { customerInfo } = useContext(CustomerInfoContext);
  const isSavingRef = useRef(false);

  const loadCounties = useCallback(async () => {
    const response = await locationService.getCounties();
    if (response.hasErrors) {
      showErrorToast(translate(resources.errors.generic));
    } else {
      setCounties(response.data);
      setFilteredCounties(response.data);
      setIsLoading(false);
    }
  }, [translate]);

  const loadCities = useCallback(
    async (countyId: number) => {
      const response = await locationService.getCities(countyId);
      if (response.hasErrors) {
        showErrorToast(translate(resources.errors.generic));
      } else {
        setCities(response.data);
        setFilteredCities(response.data);
      }
    },
    [translate],
  );

  useEffect(() => {
    if (!initialData) {
      loadCounties();
    }
  }, [initialData, loadCounties]);

  const onCountyChange = async (county: DropdownOptionModel) => {
    setCity(undefined);
    setCitiesFilter('');
    setCounty(county);
    await loadCities(county.value);
  };

  const myAccountSaveClick = async () => {
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;

    setIsButtonLoading(true);
    const response = await customerService.updateInvoicingAddress({
      countyId: county?.value,
      cityId: city?.value,
      addressLine,
      agreeCnp,
      cnp: agreeCnp ? cnp : undefined,
    });
    setIsButtonLoading(false);
    if (response.hasErrors) {
      if (response.errorType === ErrorType.ValidationError) {
        setValidationErrors(response, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      closeDialog({
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        county: county!,
        counties: counties!,
        city: city!,
        cities: cities!,
        addressLine: addressLine!,
        cnp: cnp ?? null,
      });
      showSuccessToast(translate(resources.saveConfirmation));
    }
    isSavingRef.current = false;
  };

  const cartSaveClick = async () => {
    setIsButtonLoading(true);
    const response = await customerService.validateInvoicingAddress({
      countyId: county?.value,
      cityId: city?.value,
      addressLine,
      agreeCnp,
      cnp: agreeCnp ? cnp : undefined,
    });
    setIsButtonLoading(false);

    if (response.hasErrors) {
      if (response.errorType === ErrorType.ValidationError) {
        setValidationErrors(response, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      onSaveInCart?.({
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        county: county!,
        counties: counties!,
        cities: cities!,
        city: city!,
        addressLine: addressLine!,
        agreeCnp,
        cnp: agreeCnp ? cnp : undefined,
      });
      closeDialog();
    }
  };

  const onSaveClick = () => {
    if (contextType === InvoicingAddressContextTypes.MyAccount) {
      myAccountSaveClick();
    }
    if (
      contextType === InvoicingAddressContextTypes.CartMainAddress ||
      contextType === InvoicingAddressContextTypes.CartOtherAddress
    ) {
      cartSaveClick();
    }
  };

  const clearErrors = (name: string) => {
    setErrors(
      produce(draft => {
        if (draft) {
          draft[name] = [];
        }
      }),
    );
  };

  return (
    <>
      <Dialog
        closeDialog={closeDialog}
        showCloseIcon={!isLoading}
        className="dialog-xxl invoicing-address-dialog"
      >
        <Loader loading={isLoading} loader={<LoadingSpinner />}>
          <div className="dialog-content">
            <div className="dialog-title">
              {contextType === InvoicingAddressContextTypes.CartOtherAddress
                ? translate(
                    resources.invoicingAddress.dialog.title.otherAddress,
                  )
                : translate(resources.invoicingAddress.dialog.title.edit)}
            </div>
            <div className="row">
              <div className="col col-6 col-sm-12">
                <div className="invoicing-address-name-input-container">
                  <Input
                    name="lastName"
                    label={translate(
                      resources.invoicingAddress.dialog.lastName,
                    )}
                    value={customerInfo.lastName}
                    disabled
                  />
                  {!customerInfo?.isSavingsUser && <PopoverDialog
                    className="info-tooltip"
                    type="hover"
                    content={translate(resources.invoicingAddress.nameInfo)}
                    placement="bottom"
                    appendTo={document.body}
                    zIndex={10002}
                  >
                    <div className="invoicing-address-name-input-icon">
                      <Icons.InfoMedium />
                    </div>
                  </PopoverDialog>}
                </div>
              </div>
              <div className="col col-6 col-sm-12">
                <div className="invoicing-address-name-input-container">
                  <Input
                    name="firstName"
                    label={translate(
                      resources.invoicingAddress.dialog.firstName,
                    )}
                    value={customerInfo.firstName}
                    disabled
                  />
                  {!customerInfo?.isSavingsUser && <PopoverDialog
                    className="info-tooltip"
                    type="hover"
                    content={translate(resources.invoicingAddress.nameInfo)}
                    placement="bottom"
                    appendTo={document.body}
                    zIndex={10002}
                  >
                    <div className="invoicing-address-name-input-icon">
                      <Icons.InfoMedium />
                    </div>
                  </PopoverDialog>}
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col col-6 col-sm-12">
                <Dropdown
                  name="county"
                  label={translate(resources.invoicingAddress.dialog.county)}
                  filterable
                  onFilterChange={e =>
                    setFilteredCounties(
                      counties?.filter(c =>
                        c.text
                          .toLowerCase()
                          .includes(e.filter.value.toLowerCase()),
                      ),
                    )
                  }
                  mandatory
                  data={filteredCounties}
                  value={
                    county ?? {
                      text: translate(
                        resources.invoicingAddress.dialog.countyPlaceholder,
                      ),
                      value: null,
                    }
                  }
                  onChange={e => onCountyChange(e.value)}
                  textField="text"
                  dataItemKey="value"
                  errors={errors.county}
                  clearErrors={() => clearErrors('county')}
                  onBlur={e => {
                    if (!e.target.value || !e.target.value?.value) {
                      setErrors(
                        produce(d => {
                          d.county = [getRequiredValidationError('county')];
                        }),
                      );
                    }
                  }}
                />
              </div>
              <div className="col col-6 col-sm-12">
                <Dropdown
                  name="city"
                  label={translate(resources.invoicingAddress.dialog.city)}
                  filterable
                  onFilterChange={e => {
                    setCitiesFilter(e.filter.value);
                    setFilteredCities(
                      cities?.filter(c =>
                        c.text
                          .toLowerCase()
                          .includes(e.filter.value.toLowerCase()),
                      ),
                    );
                  }}
                  filter={citiesFilter}
                  mandatory
                  disabled={!county}
                  data={filteredCities}
                  value={
                    city ?? {
                      text: translate(
                        resources.invoicingAddress.dialog.cityPlaceholder,
                      ),
                      value: null,
                    }
                  }
                  onChange={e => setCity(e.value)}
                  textField="text"
                  dataItemKey="value"
                  errors={errors.city}
                  clearErrors={() => clearErrors('city')}
                  onBlur={e => {
                    if (!e.target.value || !e.target.value?.value) {
                      setErrors(
                        produce(d => {
                          d.city = [getRequiredValidationError('city')];
                        }),
                      );
                    }
                  }}
                />
              </div>
            </div>
            <div className="row">
              <div className="col col-12 col-sm-12">
                <Input
                  name="addressLine"
                  autoComplete="off"
                  label={translate(
                    resources.invoicingAddress.dialog.addressLine,
                  )}
                  mandatory
                  value={addressLine}
                  onChange={e => setAddressLine(e.value)}
                  errors={errors.addressLine}
                  clearErrors={() => clearErrors('addressLine')}
                  onBlur={e => {
                    if (!e.target.value) {
                      setErrors(
                        produce(d => {
                          d.addressLine = [
                            getRequiredValidationError('addressLine'),
                          ];
                        }),
                      );
                    }
                    if (e.target.value && e.target.value.length < 5) {
                      setErrors(
                        produce(d => {
                          d.addressLine = [
                            getValidationError(
                              'addressLine',
                              resources.accountInfo.errors.addressTooShort,
                              { _0: 5 },
                            ),
                          ];
                        }),
                      );
                    }
                  }}
                />
              </div>
            </div>
            <div className="row">
              <div className="col col-12 col-sm-12">
                <div className="control-group invoicing-address-agree-cnp">
                  <Checkbox
                    name="agreeCnp"
                    label={
                      (
                        <label>
                          <span>
                            {translate(
                              resources.invoicingAddress.dialog.agreeCnp1,
                            )}{' '}
                          </span>
                          <span
                            onClick={() => setShowGdprDialog(true)}
                            style={{
                              cursor: 'pointer',
                              textDecoration: 'underline',
                            }}
                          >
                            {translate(
                              resources.invoicingAddress.dialog.agreeCnp2,
                            )}
                          </span>
                        </label>
                      ) as any
                    }
                    isChecked={agreeCnp}
                    onChange={e => {
                      setAgreeCnp(e.value);
                      setCnp(undefined);
                      setErrors(
                        produce(d => {
                          d.cnp = [];
                        }),
                      );
                    }}
                  />
                </div>
              </div>
            </div>
            {agreeCnp && (
              <div className="row">
                <div className="col col-6 col-sm-12">
                  <Password
                    autoComplete="off"
                    name="cnp"
                    label={translate(resources.invoicingAddress.dialog.cnp)}
                    value={cnp ?? undefined}
                    onChange={e => setCnp(e.value)}
                    errors={errors.cnp}
                    clearErrors={() => clearErrors('cnp')}
                    onBlur={e => {
                      if (
                        !e.inputTarget.value ||
                        e.inputTarget.value.length !== 13
                      ) {
                        setErrors(
                          produce(d => {
                            d.cnp = [
                              getValidationError(
                                'cnp',
                                resources.accountInfo.errors.invalidCnp,
                              ),
                            ];
                          }),
                        );
                      }
                    }}
                  />
                </div>
              </div>
            )}

            {contextType === InvoicingAddressContextTypes.CartOtherAddress && (
              <Notification
                type={NotificationType.Info}
                scrollUp={false}
                messages={[
                  {
                    message: translate(
                      resources.invoicingAddress.dialog.dataWillNotBeSaved,
                    ),
                  },
                ]}
              />
            )}
          </div>

          <div className="dialog-actions horizontal">
            <Button
              onClick={() => closeDialog()}
              className="btn btn-outline"
              disabled={isButtonLoading}
            >
              <span className="btn-text"> {translate(resources.cancel)}</span>
            </Button>
            <Button
              onClick={onSaveClick}
              className="btn btn-solid"
              loading={isButtonLoading}
            >
              <span className="btn-text">{translate(resources.save)}</span>
            </Button>
          </div>
        </Loader>
      </Dialog>
      {showGdprDialog && (
        <CnpGdprDialog closeDialog={() => setShowGdprDialog(false)} />
      )}
    </>
  );
};
